import { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { supabase } from '../../../lib/supabase';
import { useSubjectList } from '../../../hooks/useSubjectList';
import {
  getMasteryLevel,
  hasIncorrectHistory,
  getIncorrectCount,
  getLastIncorrectAt,
  type MasteryRecord,
} from '../../../hooks/useQuizMasteryDB';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
  question: string;
  answer: string;
  choices: string[];
  explanation: string | null;
  created_at: string;
}

interface QuizListProps {
  questions: QuizQuestion[];
  loading: boolean;
  onRefresh: () => void;
  isAdmin: boolean;
  currentUserId?: string;
  userOwnedMode?: boolean;
  masteryRecord: MasteryRecord; // ← page.tsx から受け取る（自前フェッチ廃止）
}

// ─── 個別問題カード（メモ化） ──────────────────────────────────────────────
interface QuizCardProps {
  q: QuizQuestion;
  qIndex: number;
  isExpanded: boolean;
  isEditing: boolean;
  isSelected: boolean;
  bulkMode: boolean;
  isAdmin: boolean;
  masteryRecord: MasteryRecord;
  confirmDeleteId: string | null;
  deletingId: string | null;
  savingEdit: boolean;
  editState: {
    subject: string;
    subcategory: string;
    question: string;
    choices: string[];
    correctIndex: number | null;
    explanation: string;
  };
  allSubjectNames: string[];
  editSubcategories: { name: string }[];
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onOpenEdit: (q: QuizQuestion) => void;
  onCloseEdit: () => void;
  onSaveEdit: () => void;
  onConfirmDelete: (id: string | null) => void;
  onDelete: (id: string) => void;
  onEditChange: (field: string, value: string | string[] | number | null) => void;
}

const choiceLabels = ['A', 'B', 'C', 'D'];

const QuizCard = memo(({
  q, qIndex, isExpanded, isEditing, isSelected, bulkMode, isAdmin,
  masteryRecord, confirmDeleteId, deletingId, savingEdit,
  editState, allSubjectNames, editSubcategories,
  onToggleExpand, onToggleSelect, onOpenEdit, onCloseEdit, onSaveEdit,
  onConfirmDelete, onDelete, onEditChange,
}: QuizCardProps) => {
  const editRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && editRef.current) {
      setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [isEditing]);

  const level = getMasteryLevel(masteryRecord, q.id);
  const incorrectCount = getIncorrectCount(masteryRecord, q.id);
  const lastIncorrectAt = getLastIncorrectAt(masteryRecord, q.id);
  const isBookmarkedQ = masteryRecord[q.id]?.is_bookmarked;

  const formatLastIncorrect = (dateString: string) => {
    const date = new Date(dateString);
    const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (diffDays === 0) return '今日';
    if (diffDays === 1) return '昨日';
    if (diffDays < 7) return `${diffDays}日前`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}週間前`;
    return `${Math.floor(diffDays / 30)}ヶ月前`;
  };

  const formatDate = (s: string) => {
    const d = new Date(s);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };

  const hasChoices = q.choices && q.choices.length === 4;

  return (
    <div className={`bg-white rounded-xl border overflow-hidden transition-all duration-200 ${bulkMode && isSelected ? 'border-teal-400 ring-2 ring-teal-100' : 'border-gray-200 hover:border-gray-300'}`}>
      {/* ヘッダー行 */}
      <div
        className="p-3 sm:p-5 cursor-pointer select-none"
        onClick={() => bulkMode ? onToggleSelect(q.id) : (!isEditing && onToggleExpand(q.id))}
      >
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          {bulkMode && (
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shrink-0 mt-0.5">
              <i className={`text-lg sm:text-xl ${isSelected ? 'ri-checkbox-fill text-teal-600' : 'ri-checkbox-blank-line text-gray-400'}`}></i>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-2 flex-wrap">
              <span className="text-xs text-gray-400 font-mono">#{qIndex + 1}</span>
              <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full">{q.subject}</span>
              {q.subcategory && <span className="px-2 sm:px-2.5 py-0.5 bg-teal-50/60 text-teal-600 text-xs font-medium rounded-full">{q.subcategory}</span>}
              {/* 習熟度バッジ */}
              {level >= 2 ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200">
                  <i className="ri-checkbox-circle-fill text-xs text-teal-500"></i>
                  <span className="text-xs text-teal-600 font-medium hidden sm:inline">習熟済</span>
                </span>
              ) : level === 1 ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-100 border border-gray-200">
                  <i className="ri-loader-4-line text-xs text-gray-400"></i>
                  <span className="text-xs text-gray-500 font-medium hidden sm:inline">学習中</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 border border-gray-200">
                  <i className="ri-question-line text-xs text-gray-300"></i>
                  <span className="text-xs text-gray-400 font-medium hidden sm:inline">未学習</span>
                </span>
              )}
              {isBookmarkedQ && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200">
                  <i className="ri-bookmark-fill text-xs text-amber-500"></i>
                  <span className="text-xs text-amber-600 font-medium hidden sm:inline">苦手</span>
                </span>
              )}
              {incorrectCount > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-orange-50 border border-orange-200"
                  title={`間違えた回数: ${incorrectCount}回${lastIncorrectAt ? `\n最後: ${formatLastIncorrect(lastIncorrectAt)}` : ''}`}>
                  <i className="ri-error-warning-fill text-xs text-orange-500"></i>
                  <span className="text-xs text-orange-600 font-medium">×{incorrectCount}</span>
                  {lastIncorrectAt && <span className="text-xs text-orange-500 hidden lg:inline">({formatLastIncorrect(lastIncorrectAt)})</span>}
                </span>
              )}
              <span className="text-xs text-gray-400 hidden sm:inline">{formatDate(q.created_at)}</span>
            </div>
            <p className="text-xs sm:text-sm font-medium text-gray-800 line-clamp-2">{q.question}</p>
          </div>
          {!bulkMode && (
            <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center shrink-0">
              <i className={`ri-arrow-down-s-line text-base sm:text-lg text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
            </div>
          )}
        </div>
      </div>

      {/* 展開コンテンツ */}
      {!bulkMode && isExpanded && (
        <div className="border-t border-gray-200 px-3 sm:px-5 pb-3 sm:pb-5 pt-3 sm:pt-4">
          {isEditing ? (
            <div ref={editRef} className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-full">
                  <i className="ri-edit-line text-sm text-teal-600"></i>
                </div>
                <span className="text-sm font-bold text-gray-800">問題を編集</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">科目</label>
                <select value={editState.subject} onChange={(e) => onEditChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer">
                  {allSubjectNames.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              {editSubcategories.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">カテゴリ</label>
                  <select value={editState.subcategory} onChange={(e) => onEditChange('subcategory', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer">
                    <option value="">未選択</option>
                    {editSubcategories.map((sub) => <option key={sub.name} value={sub.name}>{sub.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">問題文</label>
                <textarea value={editState.question} onChange={(e) => onEditChange('question', e.target.value.slice(0, 500))}
                  rows={3} maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
                <p className="text-xs text-gray-400 mt-0.5 text-right">{editState.question.length}/500</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">選択肢</label>
                <div className="space-y-2">
                  {editState.choices.map((choice, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${editState.correctIndex === idx ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {choiceLabels[idx]}
                      </span>
                      <input type="text" value={choice}
                        onChange={(e) => {
                          const newChoices = [...editState.choices];
                          newChoices[idx] = e.target.value.slice(0, 200);
                          onEditChange('choices', newChoices);
                        }}
                        maxLength={200}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500" />
                      <button onClick={() => onEditChange('correctIndex', idx)}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${editState.correctIndex === idx ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {editState.correctIndex === idx ? '正解 ✓' : '正解に設定'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">解説（任意）</label>
                <textarea value={editState.explanation} onChange={(e) => onEditChange('explanation', e.target.value.slice(0, 500))}
                  rows={2} maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={onCloseEdit}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  キャンセル
                </button>
                <button onClick={onSaveEdit} disabled={savingEdit || !editState.subject || !editState.question.trim() || editState.choices.some((c) => !c.trim()) || editState.correctIndex === null}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                  {savingEdit ? '保存中...' : '保存する'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {hasChoices && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 mb-1">選択肢</p>
                  {q.choices.map((choice, idx) => {
                    const isCorrect = choice === q.answer;
                    return (
                      <div key={idx} className={`flex items-center gap-2 sm:gap-2.5 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm ${isCorrect ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-200'}`}>
                        <span className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${isCorrect ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                          {choiceLabels[idx]}
                        </span>
                        <span className={`flex-1 ${isCorrect ? 'text-teal-800 font-medium' : 'text-gray-700'}`}>{choice}</span>
                        {isCorrect && <i className="ri-checkbox-circle-fill text-teal-600 shrink-0"></i>}
                      </div>
                    );
                  })}
                </div>
              )}
              {!hasChoices && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5 sm:p-3">
                  <p className="text-xs font-semibold text-teal-700 mb-1">解答</p>
                  <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap">{q.answer}</p>
                </div>
              )}
              {q.explanation && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">解説</p>
                  <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">{q.explanation}</p>
                </div>
              )}
              {isAdmin && (
                <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
                  <button onClick={() => onOpenEdit(q)}
                    className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-edit-line text-sm"></i>編集
                  </button>
                  {confirmDeleteId === q.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-500">削除しますか？</span>
                      <button onClick={() => onConfirmDelete(null)} className="px-2.5 sm:px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">いいえ</button>
                      <button onClick={() => onDelete(q.id)} disabled={deletingId === q.id}
                        className="px-2.5 sm:px-3 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                        {deletingId === q.id ? '削除中...' : 'はい'}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => onConfirmDelete(q.id)}
                      className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 hover:bg-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-delete-bin-line text-sm"></i>削除
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
QuizCard.displayName = 'QuizCard';

// ─── メインコンポーネント ──────────────────────────────────────────────────
export default function QuizList({ questions, loading, onRefresh, isAdmin, currentUserId, userOwnedMode, masteryRecord }: QuizListProps) {
  const { subjects: allSubjectNames } = useSubjectList();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState({
    subject: '', subcategory: '', question: '',
    choices: ['', '', '', ''], correctIndex: null as number | null, explanation: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Bulk selection
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkSubcategory, setBulkSubcategory] = useState('');
  const [savingBulk, setSavingBulk] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Mastery filter
  const [masteryFilter, setMasteryFilter] = useState<'all' | 'unlearned' | 'learning' | 'mastered' | 'incorrect'>('all');
  const [incorrectSubjectFilter, setIncorrectSubjectFilter] = useState('all');
  const [incorrectSubcategoryFilter, setIncorrectSubcategoryFilter] = useState('all');

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ─── グループ化（メモ化） ────────────────────────────────────────────────
  const groupedBySubject = useMemo(() => {
    const map: Record<string, string[]> = {};
    questions.forEach((q) => { if (!map[q.subject]) map[q.subject] = []; map[q.subject].push(q.id); });
    return map;
  }, [questions]);

  const groupedBySubcategory = useMemo(() => {
    const map: Record<string, string[]> = {};
    questions.forEach((q) => {
      const key = q.subcategory ? `${q.subject}>${q.subcategory}` : q.subject;
      if (!map[key]) map[key] = [];
      map[key].push(q.id);
    });
    return map;
  }, [questions]);

  const uniqueSubjects = useMemo(() => Object.keys(groupedBySubject), [groupedBySubject]);
  const uniqueSubcategories = useMemo(() => {
    const result: { key: string; label: string }[] = [];
    questions.forEach((q) => {
      if (q.subcategory) {
        const key = `${q.subject}>${q.subcategory}`;
        if (!result.find((r) => r.key === key)) result.push({ key, label: q.subcategory });
      }
    });
    return result;
  }, [questions]);

  // ─── 習熟度フィルター済み問題（メモ化） ──────────────────────────────────
  const incorrectQuestions = useMemo(
    () => questions.filter((q) => hasIncorrectHistory(masteryRecord, q.id)),
    [questions, masteryRecord]
  );

  const incorrectSubjects = useMemo(() => {
    const s = new Set<string>();
    incorrectQuestions.forEach((q) => s.add(q.subject));
    return Array.from(s);
  }, [incorrectQuestions]);

  const incorrectSubcategoriesForSubject = useMemo(() => {
    if (incorrectSubjectFilter === 'all') return [];
    const s = new Set<string>();
    incorrectQuestions.filter((q) => q.subject === incorrectSubjectFilter && q.subcategory).forEach((q) => s.add(q.subcategory!));
    return Array.from(s);
  }, [incorrectQuestions, incorrectSubjectFilter]);

  const filteredQuestions = useMemo(() => {
    if (masteryFilter === 'all') return questions;
    return questions.filter((q) => {
      if (masteryFilter === 'incorrect') {
        if (!hasIncorrectHistory(masteryRecord, q.id)) return false;
        if (incorrectSubjectFilter !== 'all' && q.subject !== incorrectSubjectFilter) return false;
        if (incorrectSubcategoryFilter !== 'all' && (q.subcategory || '') !== incorrectSubcategoryFilter) return false;
        return true;
      }
      const level = getMasteryLevel(masteryRecord, q.id);
      if (masteryFilter === 'unlearned') return level === 0;
      if (masteryFilter === 'learning') return level === 1;
      if (masteryFilter === 'mastered') return level >= 2;
      return true;
    });
  }, [questions, masteryFilter, masteryRecord, incorrectSubjectFilter, incorrectSubcategoryFilter]);

  // ─── 集計（メモ化） ──────────────────────────────────────────────────────
  const masteryStats = useMemo(() => ({
    unlearned: questions.filter((q) => getMasteryLevel(masteryRecord, q.id) === 0).length,
    learning: questions.filter((q) => getMasteryLevel(masteryRecord, q.id) === 1).length,
    mastered: questions.filter((q) => getMasteryLevel(masteryRecord, q.id) >= 2).length,
    incorrect: incorrectQuestions.length,
  }), [questions, masteryRecord, incorrectQuestions]);

  // ─── コールバック ────────────────────────────────────────────────────────
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }, []);

  const handleOpenEdit = useCallback((q: QuizQuestion) => {
    const c = q.choices?.length === 4 ? [...q.choices] : ['', '', '', ''];
    const ci = c.findIndex((ch) => ch === q.answer);
    setEditingId(q.id);
    setEditState({ subject: q.subject, subcategory: q.subcategory ?? '', question: q.question, choices: c, correctIndex: ci >= 0 ? ci : null, explanation: q.explanation ?? '' });
    setExpandedId(q.id);
  }, []);

  const handleCloseEdit = useCallback(() => setEditingId(null), []);

  const handleEditChange = useCallback((field: string, value: string | string[] | number | null) => {
    setEditState((prev) => {
      if (field === 'subject') return { ...prev, subject: value as string, subcategory: '' };
      return { ...prev, [field]: value };
    });
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId || !editState.subject || !editState.question.trim() || editState.choices.some((c) => !c.trim()) || editState.correctIndex === null) return;
    setSavingEdit(true);
    try {
      let query = supabase.from('quiz_questions').update({
        subject: editState.subject, subcategory: editState.subcategory || null,
        question: editState.question.trim(), choices: editState.choices.map((c) => c.trim()),
        answer: editState.choices[editState.correctIndex].trim(),
        explanation: editState.explanation.trim() || null,
      }).eq('id', editingId);
      if (userOwnedMode && currentUserId) query = query.eq('user_id', currentUserId);
      const { error } = await query;
      if (error) throw error;
      setEditingId(null);
      setToast({ message: '問題を更新しました', type: 'success' });
      onRefresh();
    } catch {
      setToast({ message: '更新に失敗しました', type: 'error' });
    } finally {
      setSavingEdit(false);
    }
  }, [editingId, editState, userOwnedMode, currentUserId, onRefresh]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      let query = supabase.from('quiz_questions').delete().eq('id', id);
      if (userOwnedMode && currentUserId) query = query.eq('user_id', currentUserId);
      const { error } = await query;
      if (error) throw error;
      setConfirmDeleteId(null);
      setToast({ message: '問題を削除しました', type: 'success' });
      onRefresh();
    } catch {
      setToast({ message: '削除に失敗しました', type: 'error' });
    } finally {
      setDeletingId(null);
    }
  }, [userOwnedMode, currentUserId, onRefresh]);

  const handleConfirmDelete = useCallback((id: string | null) => setConfirmDeleteId(id), []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => prev.size === questions.length ? new Set() : new Set(questions.map((q) => q.id)));
  }, [questions]);

  const toggleSelectBySubject = useCallback((subject: string) => {
    const ids = groupedBySubject[subject] || [];
    const allSel = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => { const next = new Set(prev); allSel ? ids.forEach((id) => next.delete(id)) : ids.forEach((id) => next.add(id)); return next; });
  }, [groupedBySubject, selectedIds]);

  const toggleSelectBySubcategory = useCallback((key: string) => {
    const ids = groupedBySubcategory[key] || [];
    const allSel = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => { const next = new Set(prev); allSel ? ids.forEach((id) => next.delete(id)) : ids.forEach((id) => next.add(id)); return next; });
  }, [groupedBySubcategory, selectedIds]);

  const exitBulkMode = useCallback(() => { setBulkMode(false); setSelectedIds(new Set()); }, []);

  const handleSaveBulk = useCallback(async () => {
    if (selectedIds.size === 0 || !bulkSubject) return;
    setSavingBulk(true);
    try {
      const { error } = await supabase.from('quiz_questions').update({ subject: bulkSubject, subcategory: bulkSubcategory || null }).in('id', Array.from(selectedIds));
      if (error) throw error;
      setShowBulkEdit(false); setBulkSubject(''); setBulkSubcategory('');
      exitBulkMode();
      setToast({ message: `${selectedIds.size}件のカテゴリを変更しました`, type: 'success' });
      onRefresh();
    } catch {
      setToast({ message: '一括変更に失敗しました', type: 'error' });
    } finally {
      setSavingBulk(false);
    }
  }, [selectedIds, bulkSubject, bulkSubcategory, exitBulkMode, onRefresh]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeletingBulk(true);
    try {
      let query = supabase.from('quiz_questions').delete().in('id', Array.from(selectedIds));
      if (userOwnedMode && currentUserId) query = query.eq('user_id', currentUserId);
      const { error } = await query;
      if (error) throw error;
      setShowBulkDelete(false); exitBulkMode();
      setToast({ message: `${selectedIds.size}件の問題を削除しました`, type: 'success' });
      onRefresh();
    } catch {
      setToast({ message: '一括削除に失敗しました', type: 'error' });
    } finally {
      setDeletingBulk(false);
    }
  }, [selectedIds, userOwnedMode, currentUserId, exitBulkMode, onRefresh]);

  // ─── 編集中のサブカテゴリ ────────────────────────────────────────────────
  const editSubcategories = useMemo(() => {
    const s = new Set<string>();
    questions.forEach((q) => { if (q.subject === editState.subject && q.subcategory) s.add(q.subcategory); });
    return Array.from(s).sort().map((name) => ({ name }));
  }, [questions, editState.subject]);

  const bulkSubcategories = useMemo(() => {
    const s = new Set<string>();
    questions.forEach((q) => { if (q.subject === bulkSubject && q.subcategory) s.add(q.subcategory); });
    return Array.from(s).sort().map((name) => ({ name }));
  }, [questions, bulkSubject]);

  if (loading) {
    return <div className="text-center py-16"><div className="w-10 h-10 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div></div>;
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 sm:w-24 h-20 sm:h-24 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-4 sm:mb-6">
          <i className="ri-question-line text-3xl sm:text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">まだクイズがありません</h3>
        <p className="text-sm sm:text-base text-gray-600">{isAdmin ? '右上の「クイズを作成」ボタンから問題を追加してください。' : 'クイズはまだ登録されていません。'}</p>
      </div>
    );
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-in px-4 w-full max-w-md">
          <div className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl shadow-lg text-xs sm:text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <i className={`${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-sm sm:text-base`}></i>
            {toast.message}
          </div>
        </div>
      )}

      {/* 習熟度フィルター */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2"><span className="text-xs font-medium text-gray-500">絞り込み:</span></div>
        <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          {([
            { key: 'all', label: 'すべて', icon: 'ri-list-check', count: questions.length, color: 'teal' },
            { key: 'unlearned', label: '未学習', icon: 'ri-question-line', count: masteryStats.unlearned, color: 'gray' },
            { key: 'learning', label: '学習中', icon: 'ri-loader-4-line', count: masteryStats.learning, color: 'gray' },
            { key: 'mastered', label: '習熟済', icon: 'ri-checkbox-circle-fill', count: masteryStats.mastered, color: 'teal' },
            { key: 'incorrect', label: '間違えた問題', icon: 'ri-error-warning-fill', count: masteryStats.incorrect, color: 'orange' },
          ] as const).map(({ key, label, icon, count, color }) => (
            <button key={key}
              onClick={() => { setMasteryFilter(key); setIncorrectSubjectFilter('all'); setIncorrectSubcategoryFilter('all'); }}
              className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
                masteryFilter === key
                  ? color === 'orange' ? 'bg-orange-600 text-white' : 'bg-teal-600 text-white'
                  : color === 'orange' ? 'bg-orange-50 text-orange-600 border border-orange-300 hover:bg-orange-100' : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
              }`}>
              <i className={`${icon} text-sm`}></i>{label}<span className="opacity-70">({count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* 間違えた問題の科目フィルター */}
      {masteryFilter === 'incorrect' && incorrectQuestions.length > 0 && (
        <div className="mb-4 p-3 bg-orange-50/60 border border-orange-200 rounded-xl space-y-2.5">
          <p className="text-xs font-medium text-orange-700 flex items-center gap-1.5"><i className="ri-filter-3-line text-sm"></i>苦手分野で絞り込み</p>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => { setIncorrectSubjectFilter('all'); setIncorrectSubcategoryFilter('all'); }}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${incorrectSubjectFilter === 'all' ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
              全科目<span className="opacity-70">({incorrectQuestions.length})</span>
            </button>
            {incorrectSubjects.map((subj) => (
              <button key={subj} onClick={() => { setIncorrectSubjectFilter(subj); setIncorrectSubcategoryFilter('all'); }}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${incorrectSubjectFilter === subj ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                {subj}<span className="opacity-70">({incorrectQuestions.filter((q) => q.subject === subj).length})</span>
              </button>
            ))}
          </div>
          {incorrectSubcategoriesForSubject.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-orange-200/60">
              <button onClick={() => setIncorrectSubcategoryFilter('all')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${incorrectSubcategoryFilter === 'all' ? 'bg-orange-400 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                全カテゴリ
              </button>
              {incorrectSubcategoriesForSubject.map((subcat) => (
                <button key={subcat} onClick={() => setIncorrectSubcategoryFilter(subcat)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${incorrectSubcategoryFilter === subcat ? 'bg-orange-400 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                  {subcat}<span className="opacity-70">({incorrectQuestions.filter((q) => q.subject === incorrectSubjectFilter && q.subcategory === subcat).length})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 一括操作 */}
      {isAdmin && (
        <div className="flex items-center justify-end gap-2 mb-4 flex-wrap">
          {bulkMode ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={toggleSelectAll}
                className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0 ${selectedIds.size === questions.length ? 'bg-teal-100 text-teal-700 border border-teal-300' : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'}`}>
                <i className={`${selectedIds.size === questions.length ? 'ri-checkbox-fill text-teal-600' : 'ri-checkbox-blank-line'} text-sm`}></i>全選択
              </button>
              <button onClick={exitBulkMode} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-close-line text-sm"></i>終了
              </button>
            </div>
          ) : (
            <button onClick={() => setBulkMode(true)} className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-checkbox-multiple-line text-sm"></i>一括操作
            </button>
          )}
        </div>
      )}

      {/* 一括選択チップ */}
      {bulkMode && (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
          <p className="text-xs font-medium text-gray-500 mb-1.5"><i className="ri-folder-line mr-1 text-teal-500"></i>科目ごとに選択</p>
          <div className="flex flex-wrap gap-1.5">
            {uniqueSubjects.map((subj) => {
              const ids = groupedBySubject[subj] || [];
              const allSel = ids.every((id) => selectedIds.has(id));
              const someSel = ids.some((id) => selectedIds.has(id));
              return (
                <button key={subj} onClick={() => toggleSelectBySubject(subj)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${allSel ? 'bg-teal-500 text-white' : someSel ? 'bg-teal-100 text-teal-700 border border-teal-300' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                  {subj}<span className="opacity-70">({ids.length})</span>
                </button>
              );
            })}
          </div>
          {uniqueSubcategories.length > 0 && (
            <>
              <p className="text-xs font-medium text-gray-500 mt-2 mb-1.5"><i className="ri-price-tag-3-line mr-1 text-teal-500"></i>カテゴリごとに選択</p>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {uniqueSubcategories.map((sc) => {
                  const ids = groupedBySubcategory[sc.key] || [];
                  const allSel = ids.every((id) => selectedIds.has(id));
                  const someSel = ids.some((id) => selectedIds.has(id));
                  return (
                    <button key={sc.key} onClick={() => toggleSelectBySubcategory(sc.key)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${allSel ? 'bg-teal-500 text-white' : someSel ? 'bg-teal-100 text-teal-700 border border-teal-300' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}>
                      {sc.label}<span className="opacity-70">({ids.length})</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {filteredQuestions.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 sm:w-20 h-16 sm:h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3 sm:mb-4">
            <i className="ri-filter-off-line text-2xl sm:text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">該当する問題がありません</h3>
          <p className="text-xs sm:text-sm text-gray-600">{masteryFilter === 'all' ? 'クイズがまだ登録されていません。' : '別のフィルターを選択してください。'}</p>
        </div>
      )}

      {/* 問題リスト */}
      <div className="space-y-3">
        {filteredQuestions.map((q, qIndex) => (
          <QuizCard
            key={q.id}
            q={q}
            qIndex={qIndex}
            isExpanded={expandedId === q.id}
            isEditing={editingId === q.id}
            isSelected={selectedIds.has(q.id)}
            bulkMode={bulkMode}
            isAdmin={isAdmin}
            masteryRecord={masteryRecord}
            confirmDeleteId={confirmDeleteId}
            deletingId={deletingId}
            savingEdit={savingEdit}
            editState={editState}
            allSubjectNames={allSubjectNames}
            editSubcategories={editSubcategories}
            onToggleExpand={handleToggleExpand}
            onToggleSelect={handleToggleSelect}
            onOpenEdit={handleOpenEdit}
            onCloseEdit={handleCloseEdit}
            onSaveEdit={handleSaveEdit}
            onConfirmDelete={handleConfirmDelete}
            onDelete={handleDelete}
            onEditChange={handleEditChange}
          />
        ))}
      </div>

      {/* 一括操作フローティングバー */}
      {bulkMode && selectedIds.size > 0 && (
        <div className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-800 text-white rounded-2xl shadow-2xl px-3 sm:px-5 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 animate-fade-in max-w-[calc(100vw-2rem)]">
          <span className="text-xs sm:text-sm font-medium whitespace-nowrap"><span className="text-teal-300 font-bold">{selectedIds.size}</span> 件選択中</span>
          <div className="w-px h-5 sm:h-6 bg-gray-600"></div>
          <button onClick={() => { setBulkSubject(allSubjectNames[0] || ''); setBulkSubcategory(''); setShowBulkEdit(true); }}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-edit-line text-xs sm:text-sm"></i><span className="hidden sm:inline">カテゴリ変更</span><span className="sm:hidden">変更</span>
          </button>
          <button onClick={() => setShowBulkDelete(true)}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-delete-bin-line text-xs sm:text-sm"></i><span className="hidden sm:inline">一括削除</span><span className="sm:hidden">削除</span>
          </button>
          <button onClick={exitBulkMode} className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg transition-colors cursor-pointer shrink-0">
            <i className="ri-close-line text-base sm:text-lg"></i>
          </button>
        </div>
      )}

      {/* 一括削除確認モーダル */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkDelete(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-5">
              <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-3"><i className="ri-delete-bin-line text-2xl text-red-500"></i></div>
              <h3 className="text-base font-bold text-gray-800">一括削除の確認</h3>
              <p className="text-sm text-gray-500 mt-2">選択した <span className="font-bold text-red-500">{selectedIds.size}件</span> の問題を削除します。<br />この操作は取り消せません。</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowBulkDelete(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">キャンセル</button>
              <button onClick={handleBulkDelete} disabled={deletingBulk} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {deletingBulk ? '削除中...' : `${selectedIds.size}件を削除`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 一括カテゴリ変更モーダル */}
      {showBulkEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkEdit(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-full shrink-0"><i className="ri-checkbox-multiple-fill text-lg text-teal-600"></i></div>
              <div>
                <h3 className="text-base font-bold text-gray-800">カテゴリを一括変更</h3>
                <p className="text-xs text-gray-500">選択した <span className="font-bold text-teal-600">{selectedIds.size}件</span> の問題を一括で変更します</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">科目</label>
              <select value={bulkSubject} onChange={(e) => { setBulkSubject(e.target.value); setBulkSubcategory(''); }}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer">
                {allSubjectNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            {bulkSubcategories.length > 0 && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">カテゴリ</label>
                <select value={bulkSubcategory} onChange={(e) => setBulkSubcategory(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer">
                  <option value="">未選択</option>
                  {bulkSubcategories.map((sub) => <option key={sub.name} value={sub.name}>{sub.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowBulkEdit(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">キャンセル</button>
              <button onClick={handleSaveBulk} disabled={savingBulk || !bulkSubject} className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                {savingBulk ? '保存中...' : `${selectedIds.size}件を変更`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
